import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { Wrench, Settings, Cog } from "lucide-react";

const services = [
  {
    name: "Spindle Repair",
    description: "Professional spindle repair and refurbishment services for textile spinning machinery. We restore worn spindles to original specifications with precision balancing, bearing replacement, and performance optimization for extended operational life.",
    features: "Complete overhaul, precision balancing, bearing replacement, performance testing",
    icon: <Wrench className="h-8 w-8 text-primary" />,
    imageUrl: "/images/spindle-service.jpg"
  },
  {
    name: "Bobbin Holder Repair",
    description: "Expert bobbin holder repair and restoration services. We refurbish worn bobbin holder assemblies, replace damaged components, and restore proper functionality to ensure smooth yarn winding and consistent tension control.",
    features: "Component replacement, surface refinishing, assembly restoration, quality inspection",
    icon: <Settings className="h-8 w-8 text-primary" />,
    imageUrl: "/images/bobbin-holder-service.jpg"
  },
  {
    name: "Top Arm Repair",
    description: "Comprehensive top arm repair services for spinning frame machinery. Our skilled technicians restore structural integrity, replace worn components, and ensure proper alignment for reliable long-term operation.",
    features: "Structural assessment, component replacement, precision alignment, quality testing",
    icon: <Cog className="h-8 w-8 text-primary" />,
    imageUrl: "/images/top-arm-service.jpg"
  }
];

export default function Services() {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section 
        className="relative flex min-h-[400px] items-center justify-center bg-gradient-to-r from-slate-900 to-slate-700 text-white"
        style={{
          backgroundImage: "url('/images/ring-frame-machine-hq.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundBlendMode: "overlay"
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60"></div>
        <div className="container relative z-10 mx-auto px-4 md:px-6 text-center">
          <motion.h1 
            className="text-4xl font-bold leading-tight sm:text-5xl md:text-6xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Repair & Maintenance Services
          </motion.h1>
          <motion.p 
            className="mt-6 text-xl text-slate-100 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Professional repair and maintenance services for spinning machine components. 
            Our expert technicians ensure your machinery operates at peak performance with minimal downtime.
          </motion.p>
        </div>
      </section>

      <div className="container py-16 px-4 md:px-6">
        {/* Our Services Header */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h2 className="text-3xl font-bold tracking-tight text-center mb-4">Our Repair Services</h2>
          <p className="text-lg text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed">
            Comprehensive repair solutions for spinning machine parts with over 20 years of technical expertise and precision craftsmanship.
          </p>
        </motion.div>

        {/* Services Carousel */}
        <motion.div
          className="mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent>
              {services.map((service, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                  <motion.div
                    className="p-4 cursor-pointer"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.5, 
                      delay: 0.8 + (index * 0.1),
                      ease: [0.25, 0.25, 0.25, 0.75]
                    }}
                    whileHover={{ 
                      scale: 1.30,
                      y: -25,
                      transition: { 
                        duration: 0.25, 
                        ease: "easeOut"
                      }
                    }}
                    whileTap={{ scale: 0.98 }}
                    style={{ 
                      transformOrigin: "center center"
                    }}
                  >
                    <Card className="h-full shadow-lg hover:shadow-2xl transition-all duration-500 border-0 bg-gradient-to-br from-white to-gray-50">
                      <CardContent className="p-6 flex flex-col h-full">
                        <motion.div 
                          className="aspect-video relative overflow-hidden rounded-lg mb-6 bg-gray-100"
                          whileHover={{ 
                            scale: 1.45,
                            transition: { 
                              duration: 0.2,
                              ease: "easeOut"
                            }
                          }}
                          animate={{ 
                            scale: 1,
                            transition: { 
                              duration: 0.15,
                              ease: "easeInOut"
                            }
                          }}
                        >
                          <motion.img
                            src={service.imageUrl}
                            alt={service.name}
                            className="w-full h-full object-cover"
                            style={{
                              filter: 'brightness(1.1) contrast(1.05)',
                              background: 'linear-gradient(45deg, #f8f9fa 25%, transparent 25%), linear-gradient(-45deg, #f8f9fa 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f8f9fa 75%), linear-gradient(-45deg, transparent 75%, #f8f9fa 75%)',
                              backgroundSize: '20px 20px',
                              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                            }}
                            whileHover={{
                              filter: "brightness(1.4) contrast(1.5) saturate(1.4)",
                              transition: { duration: 0.15 }
                            }}
                            animate={{
                              filter: "brightness(1.1) contrast(1.05) saturate(1.0)",
                              transition: { duration: 0.1 }
                            }}
                          />
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent opacity-0"
                            whileHover={{
                              opacity: 1,
                              transition: { duration: 0.3 }
                            }}
                          />
                        </motion.div>
                        <div className="flex items-center gap-3 mb-4">
                          {service.icon}
                          <h3 className="text-xl font-semibold">{service.name}</h3>
                        </div>
                        <p className="text-muted-foreground mb-4 flex-grow leading-relaxed">
                          {service.description}
                        </p>
                        <div className="mt-auto">
                          <div className="text-sm font-medium text-primary mb-2">Key Features:</div>
                          <div className="text-sm text-muted-foreground">
                            {service.features}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 bg-background/90 hover:bg-background shadow-lg" />
            <CarouselNext className="-right-4 bg-background/90 hover:bg-background shadow-lg" />
          </Carousel>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          <div className="bg-slate-50 rounded-lg p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold mb-4">Need Repair Services?</h3>
            <p className="text-muted-foreground leading-relaxed mb-6 max-w-2xl mx-auto">
              Contact our expert technicians for professional repair and maintenance services. 
              We provide detailed assessments, transparent pricing, and guaranteed quality workmanship.
            </p>
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <a 
                href="/contact" 
                className="inline-flex items-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                Request Service Quote
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}